// src/components/layout/ThemeToggle.tsx
// Component for switching between light and dark themes

'use client'; // Required for hooks and event handlers

import * as React from 'react';
import { Moon, Sun } from 'lucide-react'; // Icons for themes
import { useTheme } from 'next-themes'; // Hook to manage theme

import { Button } from '@/components/ui/button'; // shadcn Button
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'; // shadcn DropdownMenu

/**
 * ThemeToggle component provides a button with dropdown menu
 * to switch between light, dark, and system themes.
 */
export function ThemeToggle() {
  // useTheme hook provides access to current theme and setTheme function
  const { setTheme } = useTheme();

  return (
    <DropdownMenu>
      {/* Dropdown Trigger: The button itself */}
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          {/* Sun icon shown by default, Moon icon hidden but present for dark mode */}
          <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          {/* Moon icon hidden by default, shown in dark mode */}
          <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          {/* Screen reader accessibility */}
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>

      {/* Dropdown Content: The menu that appears on click */}
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => setTheme('light')}>
          Light
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme('dark')}>
          Dark
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme('system')}>
          System
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
